/**
 * DuplicatesDialog — admin tool to find trainee records sharing an email
 * and merge them into one primary record (history repointed, dupes removed).
 */
import { useEffect, useState, useCallback } from 'react';
import { GitMerge, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { useApi } from '../../hooks/useApi';
import MergeAllDialog, { UndoMergeBar } from './MergeAllDialog';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '../../components/ui/dialog';
import { Card } from '../../components/ui/card';

export const DuplicatesDialog = ({ onClose }) => {
  const { get, post } = useApi();
  const [mergeAllOpen, setMergeAllOpen] = useState(false);
  const [pendingUndo, setPendingUndo] = useState([]);
  const [groups, setGroups] = useState(null);
  const [primaries, setPrimaries] = useState({});
  const [merging, setMerging] = useState(null);

  const load = useCallback(() => {
    setGroups(null);
    get('/trainees/duplicates/health').then(h => setPendingUndo(h.pending_undo || [])).catch(() => {});
    get('/trainees/duplicates')
      .then(res => {
        setGroups(res.groups);
        const pre = {};
        res.groups.forEach(g => { pre[g.email] = g.trainees[0]?.trainee_id; });
        setPrimaries(pre);
      })
      .catch(e => { toast.error(e.response?.data?.detail || 'Failed to scan'); setGroups([]); });
  }, [get]);

  useEffect(() => { load(); }, [load]);

  const onMerge = async (group) => {
    const primaryId = primaries[group.email];
    const dupIds = group.trainees.map(t => t.trainee_id).filter(id => id !== primaryId);
    if (!primaryId || dupIds.length === 0) return;
    if (!window.confirm(
      `Merge ${dupIds.length} duplicate record${dupIds.length === 1 ? '' : 's'} of ${group.email} into the selected primary? ` +
      'All history moves to the primary and the duplicates are removed. This cannot be undone.')) return;
    setMerging(group.email);
    try {
      const res = await post('/trainees/merge', { primary_id: primaryId, duplicate_ids: dupIds });
      const moved = Object.values(res.history_moved || {}).reduce((a, b) => a + b, 0);
      toast.success(`Merged ${res.merged} record${res.merged === 1 ? '' : 's'} — ${moved} history item${moved === 1 ? '' : 's'} moved`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Merge failed');
    } finally { setMerging(null); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="duplicates-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <GitMerge className="w-5 h-5" /> Duplicate Trainees
          </DialogTitle>
          <DialogDescription>
            Records sharing the same email. Pick the primary (pre-selected by most activity) and merge —
            attempts, attendance, certifications, learning and notes all move to the primary.
          </DialogDescription>
        </DialogHeader>
        <div className="flex items-center justify-between gap-3">
          <p className="text-xs text-muted-foreground">Also matches Cisco/Employee ID. Oldest record keeps its ID.</p>
          <Button size="sm" variant="outline" onClick={() => setMergeAllOpen(true)} disabled={!groups || groups.length === 0}
                  data-testid="duplicates-merge-all-btn">
            Merge all by email / ID
          </Button>
        </div>
        <UndoMergeBar pending={pendingUndo} onUndone={load} />
        <MergeAllDialog open={mergeAllOpen} onOpenChange={setMergeAllOpen} onDone={load} />
        {groups === null && <p className="text-sm text-muted-foreground py-6 text-center">Scanning for duplicates…</p>}
        {groups && groups.length === 0 && (
          <p className="text-sm text-muted-foreground py-6 text-center" data-testid="no-duplicates-msg">
            No duplicate trainee records found — everything is clean.
          </p>
        )}
        <div className="space-y-4">
          {(groups || []).map(g => (
            <Card key={g.email} className="p-4" data-testid={`dup-group-${g.email}`}>
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm font-semibold">{g.email}
                  <Badge variant="outline" className="ml-2">{g.count} records</Badge>
                </p>
                <Button size="sm" onClick={() => onMerge(g)} disabled={merging === g.email}
                        data-testid={`merge-btn-${g.email}`}>
                  <GitMerge className="w-4 h-4 mr-1" />
                  {merging === g.email ? 'Merging…' : 'Merge'}
                </Button>
              </div>
              <div className="space-y-1.5">
                {g.trainees.map(t => (
                  <label key={t.trainee_id}
                         className={`flex items-center gap-3 p-2.5 border rounded-md cursor-pointer text-sm transition-colors ${primaries[g.email] === t.trainee_id ? 'border-primary bg-primary/5' : 'border-border hover:bg-muted/50'}`}
                         data-testid={`dup-option-${t.trainee_id}`}>
                    <input type="radio" name={`primary-${g.email}`}
                           checked={primaries[g.email] === t.trainee_id}
                           onChange={() => setPrimaries(p => ({ ...p, [g.email]: t.trainee_id }))} />
                    <span className="flex-1">
                      <span className="font-medium">{t.name}</span>
                      <span className="text-xs text-muted-foreground ml-2">
                        {t.employee_id || '—'} · {t.batch_name}
                      </span>
                    </span>
                    <span className="text-xs text-muted-foreground">
                      {t.attempts} attempt{t.attempts === 1 ? '' : 's'} · {t.score_fields} field{t.score_fields === 1 ? '' : 's'}
                    </span>
                    {primaries[g.email] === t.trainee_id && (
                      <Badge className="bg-emerald-100 text-emerald-800 border-0 text-[10px]">Primary</Badge>
                    )}
                    {!t.is_active && <Badge variant="outline" className="text-[10px]">Inactive</Badge>}
                  </label>
                ))}
              </div>
            </Card>
          ))}
        </div>
        <div className="flex justify-end pt-2">
          <Button variant="outline" size="sm" onClick={load} data-testid="rescan-duplicates-btn">
            <RefreshCw className="w-4 h-4 mr-1" /> Rescan
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
